import {
  type FormattedStatusTimeline,
  formatStatusTimeline,
} from '@backmarket/nuxt-layer-buyback/app/utils/adapters'
import type { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import type { Location } from '@ds/types/Location'

import type { Sale } from '~/api/buyback/order/types'

import translations from '../OrdersSection.translations'
import type { TradeInCard } from '../OrdersSection.types'
import { WEEKDAY_DATE_FORMAT } from '../OrdersSection.utils'

/** Everything the card builders need from the Nuxt setup context. */
export type TradeInCardContext = {
  i18n: ReturnType<typeof useI18n>
  tradeInsPageTo: Location
}

/**
 * Builds the trade-in cards: one per buyback order, tagged with its current
 * timeline step.
 */
export function toTradeInCards(
  results: Sale[] | undefined,
  { i18n, tradeInsPageTo }: TradeInCardContext,
): TradeInCard[] {
  if (!results?.length) return []

  return results
    .filter(
      // Only show trade-ins with a status timeline — it carries the status tag
      // and the dates shown on the card.
      (sale) => sale.order.statusTimeline.length > 0,
    )
    .map((sale) => {
      const steps = formatStatusTimeline(sale.order.statusTimeline)
      const currentStep = steps.find((step) => step.current) ?? steps[0]

      return {
        key: `trade-in-${sale.order.id}`,
        type: 'trade-in',
        // The first timeline step date is the order creation date.
        sortDate: steps[0].date,
        cardProps: {
          tagLabel: currentStep.tag,
          tagVariant: 'alternate',
          dateLabel: getTradeInDateLabel(currentStep, i18n),
          imageUrl: sale.product.image.light,
          imageAlt: sale.product.title,
          title: sale.product.title,
          to: tradeInsPageTo,
        },
      }
    })
}

function getTradeInDateLabel(
  currentStep: FormattedStatusTimeline,
  i18n: TradeInCardContext['i18n'],
): string | undefined {
  return currentStep.date
    ? i18n(translations.datePrefixOn, {
        date: i18n.date(currentStep.date, WEEKDAY_DATE_FORMAT),
      })
    : undefined
}
